import React, { useCallback, useEffect, useState } from 'react';
import { Button, Image, InputNumber, Space, Switch, Tag, Typography, message } from 'antd';
import { DownloadOutlined, QrcodeOutlined } from '@ant-design/icons';
import JSZip from 'jszip';
import PageTitle from '../../components/PageTitle';
import ListToolbar from '../../components/ListToolbar';
import ResizableTable from '../../components/ResizableTable';
import {
  downloadRentQRCode,
  fetchRentQRCodeBlob,
  generateRentQRCode,
  getRentQRCodes,
  updateRentQRCode,
} from '../../api';

const { Text, Link } = Typography;

function normalizeCode(code) {
  return String(code || '').trim().toUpperCase();
}

function buildPageZipFilename(rows) {
  const codes = rows.map((r) => normalizeCode(r.code)).filter(Boolean).sort();
  if (!codes.length) return '';
  if (codes.length === 1) return `rent_qr_${codes[0]}.zip`;
  return `rent_qr_${codes[0]}-${codes[codes.length - 1]}.zip`;
}

function base64ToBlob(b64) {
  const raw = String(b64 || '').trim();
  const bin = atob(raw);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: 'image/png' });
}

function triggerBlobDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

const RentQRCodeList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [generatingId, setGeneratingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchTip, setBatchTip] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchList = useCallback(async (page = 1, pageSize = 20, search = '') => {
    setLoading(true);
    try {
      const res = await getRentQRCodes({ page, page_size: pageSize, search });
      if (res?.success) {
        const payload = res.data || {};
        setData(Array.isArray(payload.list) ? payload.list : []);
        setPagination({
          current: payload.page || page,
          pageSize: payload.page_size || pageSize,
          total: payload.total || 0,
        });
      } else {
        message.error(res?.msg || '加载失败');
      }
    } catch (err) {
      message.error(err.response?.data?.msg || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(1, pagination.pageSize, '');
  }, [fetchList]);

  const handleSearch = () => {
    fetchList(1, pagination.pageSize, searchText.trim());
  };

  const handleReset = () => {
    setSearchText('');
    fetchList(1, pagination.pageSize, '');
  };

  const handleRefresh = () => {
    fetchList(pagination.current, pagination.pageSize, searchText.trim());
  };

  const patchRow = (id, patch) => {
    setData((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const handleStatusChange = async (record, checked) => {
    const status = checked ? 1 : 0;
    try {
      const res = await updateRentQRCode(record.id, { status });
      if (res?.success) {
        patchRow(record.id, res.data || { status });
        message.success('状态已更新');
      } else {
        message.error(res?.msg || '更新失败');
      }
    } catch (err) {
      message.error(err.response?.data?.msg || '更新失败');
    }
  };

  const handleHouseIdSave = async (record, value) => {
    const nextVal = value === '' || value == null ? null : Number(value);
    const prevVal = record.house_id == null ? null : Number(record.house_id);
    if (nextVal === prevVal) return;
    if (nextVal != null && (!Number.isFinite(nextVal) || nextVal <= 0)) {
      message.warning('房源 ID 须为正整数，留空表示未绑定');
      return;
    }
    try {
      const res = await updateRentQRCode(record.id, { house_id: nextVal });
      if (res?.success) {
        patchRow(record.id, res.data || { house_id: nextVal });
        message.success('房源 ID 已更新');
      } else {
        message.error(res?.msg || '更新失败');
      }
    } catch (err) {
      message.error(err.response?.data?.msg || '更新失败');
    }
  };

  const handleGenerate = async (record) => {
    setGeneratingId(record.id);
    try {
      const res = await generateRentQRCode(record.id);
      if (res?.success) {
        patchRow(record.id, res.data || {});
        message.success('二维码已生成');
      } else {
        message.error(res?.msg || '生成失败');
      }
    } catch (err) {
      message.error(err.response?.data?.msg || '生成失败');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleDownload = async (record) => {
    setDownloadingId(record.id);
    try {
      await downloadRentQRCode(record.id, record.code);
      message.success('下载成功');
    } catch (err) {
      message.error(err?.message || '下载失败');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleBatchGeneratePage = async () => {
    if (!data.length || batchBusy) return;
    setBatchBusy(true);
    let ok = 0;
    let fail = 0;
    try {
      for (let i = 0; i < data.length; i += 1) {
        const row = data[i];
        setBatchTip(`正在生成本页 ${i + 1}/${data.length}：${row.code || row.id}`);
        try {
          const res = await generateRentQRCode(row.id);
          if (res?.success) {
            patchRow(row.id, res.data || {});
            ok += 1;
          } else {
            fail += 1;
          }
        } catch (e) {
          fail += 1;
        }
      }
      if (fail === 0) {
        message.success(`本页 ${ok} 个二维码已全部生成`);
      } else {
        message.warning(`本页生成完成：成功 ${ok}，失败 ${fail}`);
      }
    } finally {
      setBatchTip('');
      setBatchBusy(false);
    }
  };

  const handleBatchDownloadPageZip = async () => {
    if (!data.length || batchBusy) return;
    const zipName = buildPageZipFilename(data);
    if (!zipName) {
      message.warning('当前页没有可下载的编码');
      return;
    }

    setBatchBusy(true);
    const zip = new JSZip();
    let ok = 0;
    let fail = 0;

    try {
      for (let i = 0; i < data.length; i += 1) {
        const row = data[i];
        const code = normalizeCode(row.code);
        if (!code) {
          fail += 1;
          continue;
        }
        setBatchTip(`正在打包本页 ${i + 1}/${data.length}：${code}`);
        try {
          let blob;
          const b64 = String(row.qrcode_base64 || '').trim();
          if (b64) {
            blob = base64ToBlob(b64);
          } else {
            blob = await fetchRentQRCodeBlob(row.id);
          }
          zip.file(`${code}.png`, blob);
          ok += 1;
        } catch (e) {
          fail += 1;
        }
      }

      if (ok === 0) {
        message.error('本页没有成功打包任何二维码');
        return;
      }

      setBatchTip('正在压缩 ZIP…');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      triggerBlobDownload(zipBlob, zipName);
      if (fail === 0) {
        message.success(`已下载 ${zipName}（${ok} 张）`);
      } else {
        message.warning(`已下载 ${zipName}：成功 ${ok} 张，失败 ${fail} 张`);
      }
    } finally {
      setBatchTip('');
      setBatchBusy(false);
    }
  };

  const columns = [
    {
      title: '招租编码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      fixed: 'left',
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: '房源 ID',
      dataIndex: 'house_id',
      key: 'house_id',
      width: 140,
      render: (val, record) => (
        <InputNumber
          style={{ width: '100%' }}
          min={1}
          precision={0}
          placeholder="未绑定"
          value={val ?? undefined}
          onChange={(v) => patchRow(record.id, { house_id: v ?? null })}
          onBlur={(e) => {
            const raw = String(e.target.value || '').trim();
            handleHouseIdSave(record, raw === '' ? null : raw);
          }}
          onPressEnter={(e) => {
            const raw = String(e.target.value || '').trim();
            handleHouseIdSave(record, raw === '' ? null : raw);
          }}
        />
      ),
    },
    {
      title: '是否生效',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (val, record) => (
        <Switch
          checked={Number(val) === 1}
          checkedChildren="启用"
          unCheckedChildren="停用"
          onChange={(checked) => handleStatusChange(record, checked)}
        />
      ),
    },
    {
      title: '二维码链接',
      dataIndex: 'qr_url',
      key: 'qr_url',
      width: 280,
      ellipsis: true,
      render: (val) => (
        <Link href={val} target="_blank" copyable={{ text: val }}>
          {val}
        </Link>
      ),
    },
    {
      title: '二维码',
      dataIndex: 'qrcode_base64',
      key: 'qrcode',
      width: 120,
      render: (val, record) => {
        if (!val && !record.has_qrcode) {
          return <Text type="secondary">未生成</Text>;
        }
        const src = val ? `data:image/png;base64,${val}` : '';
        return src ? (
          <Image
            src={src}
            alt={record.code}
            width={80}
            height={80}
            preview={{ src }}
            style={{ objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 4 }}
          />
        ) : (
          <Text type="secondary">已生成</Text>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<QrcodeOutlined />}
            loading={generatingId === record.id}
            disabled={batchBusy}
            onClick={() => handleGenerate(record)}
          >
            生成
          </Button>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            loading={downloadingId === record.id}
            disabled={batchBusy}
            onClick={() => handleDownload(record)}
          >
            下载
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageTitle title="二维码生成" />
      <ListToolbar
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        searchPlaceholder="搜索编码或房源 ID"
        beforeFilters={
          <Space size="small" wrap>
            <Tag color="blue">链接：https://chuzu.spsjkj.com?scene=编码</Tag>
            {batchTip ? <Tag color="processing">{batchTip}</Tag> : null}
          </Space>
        }
        actions={[
          {
            key: 'batch-generate-page',
            label: '生成本页',
            icon: <QrcodeOutlined />,
            type: 'primary',
            disabled: batchBusy || !data.length,
            onClick: handleBatchGeneratePage,
          },
          {
            key: 'batch-download-page-zip',
            label: '下载本页ZIP',
            icon: <DownloadOutlined />,
            disabled: batchBusy || !data.length,
            onClick: handleBatchDownloadPageZip,
          },
        ]}
      />
      <ResizableTable
        rowKey="id"
        loading={loading || batchBusy}
        columns={columns}
        dataSource={data}
        storageKey="rentQRCodeList_columnStates"
        scroll={{ x: 1100 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            fetchList(page, pageSize, searchText.trim());
          },
        }}
      />
    </div>
  );
};

export default RentQRCodeList;
